import {
  type ChangeEvent,
  type MouseEvent,
  type SyntheticEvent,
  useEffect,
  useRef,
  useState,
} from "react";
import {
  Alert,
  Box,
  Button,
  Container,
  IconButton,
  Link,
  Menu,
  MenuItem,
  Paper,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import AudiotrackIcon from "@mui/icons-material/Audiotrack";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { Link as RouterLink, useLocation } from "react-router-dom";
import { useAssessmentSession } from "../../contexts/assessmentUserContext";
import { getApiUrls } from "../../lib/apiUrl";
import {
  type AssessmentBenchmarkAsset,
  listAssessmentBenchmarkAssetsRequest,
} from "../../services/assessmentContentService";
import { useAssessmentBuilderSave } from "./useAssessmentBuilderSave";
import { AssessmentQuestionPreview } from "./AssessmentQuestionPreview";
import { BenchmarkAssetUploadDialog } from "./BenchmarkAssetUploadDialog";
import { YesNoQuestion as ListeningYesNoPreview } from "../Assessment/ListeningAssessment/steps/YesNoQuestion";
import { StudentSelectQuestion as ListeningStudentSelectPreview } from "../Assessment/ListeningAssessment/steps/StudentSelectQuestion";
import type { ListeningAssessmentContent } from "../Assessment/ListeningAssessment/steps";

type ImageMediaItem = {
  id: string;
  label: string;
  imageUrl: string;
  altText: string;
};

type AudioMediaItem = {
  id: string;
  label: string;
  audioUrl: string;
};

type Character = {
  id: string;
  name: string;
  image: string;
};

type CharacterGroup = {
  teacher: Character;
  students: Character[];
};

type YesNoQuestion = {
  id: string;
  image: string;
  audio: string;
  prompt: string;
  optionA: string;
  optionB: string;
  correctOption: "A" | "B";
};

type ListeningSaveYesNoQuestion = YesNoQuestion;

type StudentSelectOption = {
  id: string;
  studentId: string;
  audio: string;
};

type StudentSelectQuestion = {
  id: string;
  image: string;
  prompt: string;
  promptAudio: string;
  correctOption: string;
  options: StudentSelectOption[];
};

type ListeningAssessmentMeta = {
  difficultyLevel: string;
  assessmentTitle: string;
  overallInstructions: string;
  instructionsAudio: string;
  instructionImage: string;
  yesNoInstructions: string;
  yesNoInstructionsAudio: string;
  yesNoQuestionAudio: string;
  yesNoInstructionImage: string;
  studentSelectionInstructions: string;
  studentSelectionInstructionsAudio: string;
  studentSelectionQuestionAudio: string;
  studentSelectionInstructionImage: string;
  recapInstructions: string;
  recapInstructionsAudio: string;
  recapImage: string;
  characterGroup: CharacterGroup;
};

type ListeningAssessmentSaveMeta = Omit<
  ListeningAssessmentMeta,
  "difficultyLevel"
>;

type ListeningPart = {
  title: string;
  promptImage: string;
  promptAudio: string;
  yesNoBank: YesNoQuestion[];
  studentSelectBank: StudentSelectQuestion[];
};

type TestDetailsResponse = {
  test: {
    pk: string;
    items: Array<Record<string, unknown>>;
  };
};

type ListeningTestTransferFile = {
  version: string;
  exportedAt: string;
  test: {
    pk?: string;
    difficulty_level: string;
    test_type: "listening";
    status: string;
    benchmark: string;
    meta: ListeningAssessmentSaveMeta;
    parts: Array<{
      part: number;
      title: string;
      prompt_image: string;
      prompt_audio: string;
      questions: {
        yes_no: YesNoQuestion[];
        student_select: StudentSelectQuestion[];
      };
    }>;
  };
};

const difficultyLevelOptions: { value: string; label: string }[] = [
  { value: "A", label: "Level A - Most Beginner" },
  { value: "B", label: "Level B - Emerging" },
  { value: "C", label: "Level C - 2/3" },
  { value: "D", label: "Level D - 4/5" },
  { value: "E", label: "Level E - 6/7" },
  { value: "F", label: "Level F - 8/9" },
  { value: "G", label: "Level G - Most Advanced" },
];

const toListeningSaveMeta = ({
  difficultyLevel,
  ...saveMeta
}: ListeningAssessmentMeta): ListeningAssessmentSaveMeta => {
  void difficultyLevel;
  return saveMeta;
};

const createLocalId = (): string => globalThis.crypto.randomUUID();

const createImageMediaItem = (): ImageMediaItem => ({
  id: createLocalId(),
  label: "",
  imageUrl: "",
  altText: "",
});

const createAudioMediaItem = (): AudioMediaItem => ({
  id: createLocalId(),
  label: "",
  audioUrl: "",
});

const createCharacter = (name = ""): Character => ({
  id: createLocalId(),
  name,
  image: "",
});

const createYesNoQuestion = (): YesNoQuestion => ({
  id: createLocalId(),
  image: "",
  audio: "",
  prompt: "",
  optionA: "Yes",
  optionB: "No",
  correctOption: "A",
});

const createStudentSelectOption = (): StudentSelectOption => ({
  id: createLocalId(),
  studentId: "",
  audio: "",
});

const createStudentSelectQuestion = (): StudentSelectQuestion => ({
  id: createLocalId(),
  image: "",
  prompt: "",
  promptAudio: "",
  correctOption: "",
  options: [createStudentSelectOption()],
});

const createPart = (index: number): ListeningPart => ({
  title: `Part ${index + 1}`,
  promptImage: "",
  promptAudio: "",
  yesNoBank: [createYesNoQuestion()],
  studentSelectBank: [createStudentSelectQuestion()],
});

const createInitialParts = (): ListeningPart[] => [
  createPart(0),
  createPart(1),
  createPart(2),
];

const initialMeta: ListeningAssessmentMeta = {
  difficultyLevel: "",
  assessmentTitle: "",
  overallInstructions: "",
  instructionsAudio: "",
  instructionImage: "",
  yesNoInstructions: "",
  yesNoInstructionsAudio: "",
  yesNoQuestionAudio: "",
  yesNoInstructionImage: "",
  studentSelectionInstructions: "",
  studentSelectionInstructionsAudio: "",
  studentSelectionQuestionAudio: "",
  studentSelectionInstructionImage: "",
  recapInstructions: "",
  recapInstructionsAudio: "",
  recapImage: "",
  characterGroup: {
    teacher: createCharacter(),
    students: [createCharacter()],
  },
};

const asString = (value: unknown): string =>
  typeof value === "string" ? value : "";

const normalizeSearch = (value: string): string => value.trim().toLowerCase();

const matchesSearch = (
  search: string,
  values: Array<string | number | undefined>,
): boolean => {
  const normalizedSearch = normalizeSearch(search);
  if (!normalizedSearch) {
    return true;
  }

  return values.some((value) =>
    String(value ?? "")
      .toLowerCase()
      .includes(normalizedSearch),
  );
};

const benchmarkAssetLabel = (asset: AssessmentBenchmarkAsset): string =>
  asset.fileName || asset.key.split("/").pop() || asset.publicUrl;

export const toListeningSaveYesNoQuestion = (
  question: YesNoQuestion,
): ListeningSaveYesNoQuestion => {
  return question;
};

const resolveListeningPreviewImageUrl = (
  imageLibrary: ImageMediaItem[],
  imageIdOrUrl: string,
): string => {
  const trimmed = imageIdOrUrl.trim();
  if (!trimmed) {
    return "";
  }

  return imageLibrary.find((item) => item.id === trimmed)?.imageUrl ?? trimmed;
};

const resolveListeningPreviewAudioUrl = (
  audioLibrary: AudioMediaItem[],
  audioIdOrUrl: string,
): string => {
  const trimmed = audioIdOrUrl.trim();
  if (!trimmed) {
    return "";
  }

  return audioLibrary.find((item) => item.id === trimmed)?.audioUrl ?? trimmed;
};

const buildYesNoPreviewAssessment = (
  meta: ListeningAssessmentMeta,
  imageLibrary: ImageMediaItem[],
  audioLibrary: AudioMediaItem[],
  partIndex: number,
  question: YesNoQuestion,
  questionIndex: number,
): ListeningAssessmentContent => ({
  testType: "listening",
  overallInstructions: meta.overallInstructions,
  instructionsAudio: resolveListeningPreviewAudioUrl(
    audioLibrary,
    meta.instructionsAudio,
  ),
  instructionImage: resolveListeningPreviewImageUrl(
    imageLibrary,
    meta.instructionImage,
  ),
  yesNoInstructions: meta.yesNoInstructions,
  yesNoInstructionsAudio: resolveListeningPreviewAudioUrl(
    audioLibrary,
    meta.yesNoInstructionsAudio,
  ),
  yesNoQuestionAudio: resolveListeningPreviewAudioUrl(
    audioLibrary,
    meta.yesNoQuestionAudio,
  ),
  yesNoInstructionImage: resolveListeningPreviewImageUrl(
    imageLibrary,
    meta.yesNoInstructionImage,
  ),
  studentSelectionInstructions: meta.studentSelectionInstructions,
  studentSelectionInstructionsAudio: resolveListeningPreviewAudioUrl(
    audioLibrary,
    meta.studentSelectionInstructionsAudio,
  ),
  studentSelectionQuestionAudio: resolveListeningPreviewAudioUrl(
    audioLibrary,
    meta.studentSelectionQuestionAudio,
  ),
  studentSelectionInstructionImage: resolveListeningPreviewImageUrl(
    imageLibrary,
    meta.studentSelectionInstructionImage,
  ),
  recapInstructions: meta.recapInstructions,
  recapInstructionsAudio: resolveListeningPreviewAudioUrl(
    audioLibrary,
    meta.recapInstructionsAudio,
  ),
  recapImage: resolveListeningPreviewImageUrl(imageLibrary, meta.recapImage),
  characterGroup: meta.characterGroup,
  part: {
    testType: "listening",
    submissionId: "preview",
    partOrder: partIndex + 1,
    prompt: "",
    questions: [
      {
        question_id:
          question.id.trim() ||
          `preview-yes-no-${partIndex + 1}-${questionIndex + 1}`,
        question_type: "yes_no",
        part_order: partIndex + 1,
        question_order: questionIndex + 1,
        prompt: question.prompt,
        image: resolveListeningPreviewImageUrl(imageLibrary, question.image),
        audio: resolveListeningPreviewAudioUrl(audioLibrary, question.audio),
        optionA: question.optionA,
        optionB: question.optionB,
      },
    ],
  },
  submissionProgress: {
    submission_id: "preview",
    status: "draft",
    answers_by_question_id: {},
  },
});

const buildStudentSelectPreviewAssessment = (
  meta: ListeningAssessmentMeta,
  imageLibrary: ImageMediaItem[],
  audioLibrary: AudioMediaItem[],
  partIndex: number,
  question: StudentSelectQuestion,
  questionIndex: number,
): ListeningAssessmentContent => ({
  testType: "listening",
  overallInstructions: meta.overallInstructions,
  instructionsAudio: resolveListeningPreviewAudioUrl(
    audioLibrary,
    meta.instructionsAudio,
  ),
  instructionImage: resolveListeningPreviewImageUrl(
    imageLibrary,
    meta.instructionImage,
  ),
  yesNoInstructions: meta.yesNoInstructions,
  yesNoInstructionsAudio: resolveListeningPreviewAudioUrl(
    audioLibrary,
    meta.yesNoInstructionsAudio,
  ),
  yesNoQuestionAudio: resolveListeningPreviewAudioUrl(
    audioLibrary,
    meta.yesNoQuestionAudio,
  ),
  yesNoInstructionImage: resolveListeningPreviewImageUrl(
    imageLibrary,
    meta.yesNoInstructionImage,
  ),
  studentSelectionInstructions: meta.studentSelectionInstructions,
  studentSelectionInstructionsAudio: resolveListeningPreviewAudioUrl(
    audioLibrary,
    meta.studentSelectionInstructionsAudio,
  ),
  studentSelectionQuestionAudio: resolveListeningPreviewAudioUrl(
    audioLibrary,
    meta.studentSelectionQuestionAudio,
  ),
  studentSelectionInstructionImage: resolveListeningPreviewImageUrl(
    imageLibrary,
    meta.studentSelectionInstructionImage,
  ),
  recapInstructions: meta.recapInstructions,
  recapInstructionsAudio: resolveListeningPreviewAudioUrl(
    audioLibrary,
    meta.recapInstructionsAudio,
  ),
  recapImage: resolveListeningPreviewImageUrl(imageLibrary, meta.recapImage),
  characterGroup: meta.characterGroup,
  part: {
    testType: "listening",
    submissionId: "preview",
    partOrder: partIndex + 1,
    prompt: "",
    questions: [
      {
        question_id:
          question.id.trim() ||
          `preview-student-select-${partIndex + 1}-${questionIndex + 1}`,
        question_type: "student_select",
        part_order: partIndex + 1,
        question_order: questionIndex + 1,
        prompt: question.prompt,
        image: resolveListeningPreviewImageUrl(imageLibrary, question.image),
        promptAudio: resolveListeningPreviewAudioUrl(
          audioLibrary,
          question.promptAudio,
        ),
        options: question.options.map((option, optionIndex) => {
          const selectedStudent = meta.characterGroup.students.find(
            (student) => student.id === option.studentId,
          );
          return {
            id: option.id || `preview-option-${optionIndex + 1}`,
            label: selectedStudent?.name || `Student ${optionIndex + 1}`,
            audio: resolveListeningPreviewAudioUrl(audioLibrary, option.audio),
            studentImage: resolveListeningPreviewImageUrl(
              imageLibrary,
              selectedStudent?.image ?? "",
            ),
          };
        }),
      },
    ],
  },
  submissionProgress: {
    submission_id: "preview",
    status: "draft",
    answers_by_question_id: {},
  },
});

const parseBenchmarkFromPK = (pk: string): string => {
  const match = pk.trim().match(/#BENCHMARK#([^#]+)$/i);
  return match?.[1] ?? "";
};

const parseDifficultyLevelFromPK = (pk: string): string => {
  const match = pk.trim().match(/^TEST#TYPE#[^#]+#DIFFICULTY#([^#]+)/i);
  return match?.[1] ?? "";
};

const partIndexFromQuestion = (item: Record<string, unknown>): number => {
  const partOrder = Number(item.part_order);
  if (!Number.isNaN(partOrder) && partOrder >= 1 && partOrder <= 3) {
    return partOrder - 1;
  }
  const sk = asString(item.SK);
  const match = sk.match(/^QUESTION#(\d+)#/);
  if (match) {
    const parsed = Number(match[1]);
    if (!Number.isNaN(parsed) && parsed >= 1 && parsed <= 3) {
      return parsed - 1;
    }
  }
  return 0;
};

export function ListeningAssessmentBuilderPage() {
  const { search } = useLocation();
  const { token } = useAssessmentSession();
  const { assessmentsApiUrl: apiUrl } = getApiUrls();

  const [meta, setMeta] = useState<ListeningAssessmentMeta>(initialMeta);
  const [parts, setParts] = useState<ListeningPart[]>(createInitialParts());
  const [imageLibrary, setImageLibrary] = useState<ImageMediaItem[]>([
    createImageMediaItem(),
  ]);
  const [audioLibrary, setAudioLibrary] = useState<AudioMediaItem[]>([
    createAudioMediaItem(),
  ]);
  const [activeTab, setActiveTab] = useState<string>("meta");
  const [collapsedYesNoQuestions, setCollapsedYesNoQuestions] = useState<
    Record<string, boolean>
  >({});
  const [collapsedStudentSelectQuestions, setCollapsedStudentSelectQuestions] =
    useState<Record<string, boolean>>({});
  const [visibleYesNoPreviews, setVisibleYesNoPreviews] = useState<
    Record<string, boolean>
  >({});
  const [visibleStudentSelectPreviews, setVisibleStudentSelectPreviews] =
    useState<Record<string, boolean>>({});
  const [defaultCollapsedAppliedByPart, setDefaultCollapsedAppliedByPart] =
    useState<Record<number, boolean>>({});
  const [isCharacterGroupCollapsed, setIsCharacterGroupCollapsed] =
    useState<boolean>(true);
  const [imageLibrarySearch, setImageLibrarySearch] = useState<string>("");
  const [audioLibrarySearch, setAudioLibrarySearch] = useState<string>("");
  const [yesNoSearchByPart, setYesNoSearchByPart] = useState<
    Record<number, string>
  >({});
  const [studentSelectSearchByPart, setStudentSelectSearchByPart] = useState<
    Record<number, string>
  >({});
  const [loadingExistingTest, setLoadingExistingTest] =
    useState<boolean>(false);
  const [existingTestError, setExistingTestError] = useState<string | null>(
    null,
  );
  const [importMessage, setImportMessage] = useState<string | null>(null);
  const [importError, setImportError] = useState<string | null>(null);
  const [moreMenuAnchorEl, setMoreMenuAnchorEl] = useState<HTMLElement | null>(
    null,
  );
  const [isAssetUploadDialogOpen, setIsAssetUploadDialogOpen] =
    useState<boolean>(false);
  const [benchmarkAssets, setBenchmarkAssets] = useState<
    AssessmentBenchmarkAsset[]
  >([]);
  const [benchmarkAssetsError, setBenchmarkAssetsError] = useState<
    string | null
  >(null);
  const [benchmarkAssetsRefreshKey, setBenchmarkAssetsRefreshKey] =
    useState<number>(0);
  const importInputRef = useRef<HTMLInputElement>(null);

  const {
    benchmark,
    setBenchmark,
    isDemo,
    setIsDemo,
    existingTestPk,
    setExistingTestPk,
    isSavingTest,
    saveMessage,
    saveError,
    setSaveError,
    saveTest,
  } = useAssessmentBuilderSave({
    search,
    token,
    apiUrl,
    testType: "listening",
    difficultyLevel: meta.difficultyLevel,
    buildPayload: ({
      existingTestPk: nextExistingTestPk,
      benchmark: nextBenchmark,
    }) => ({
      is_demo: isDemo ? JSON.parse(isDemo) : undefined,
      pk: nextExistingTestPk || undefined,
      difficulty_level: meta.difficultyLevel,
      test_type: "listening",
      status: "draft",
      benchmark: nextBenchmark,
      meta: {
        ...toListeningSaveMeta(meta),
        image_library: imageLibrary,
        audio_library: audioLibrary,
      },
      media_library: [
        ...imageLibrary.map((item) => ({
          id: item.id,
          label: item.label,
          imageUrl: item.imageUrl,
          altText: item.altText,
          mediaType: "image",
        })),
        ...audioLibrary.map((item) => ({
          id: item.id,
          label: item.label,
          audioUrl: item.audioUrl,
          mediaType: "audio",
        })),
      ],
      parts: parts.map((part, index) => ({
        part: index + 1,
        title: part.title,
        prompt_image: part.promptImage,
        prompt_audio: part.promptAudio,
        questions: {
          yes_no: part.yesNoBank.map(toListeningSaveYesNoQuestion),
          student_select: part.studentSelectBank.map((question) => ({
            id: question.id,
            image: question.image,
            prompt: question.prompt,
            promptAudio: question.promptAudio,
            correct_option: question.correctOption,
            options: question.options,
          })),
        },
      })),
    }),
  });

  const canListBenchmarkAssets = Boolean(apiUrl && token && benchmark.trim());
  const mediaLibrary = canListBenchmarkAssets ? benchmarkAssets : [];
  const benchmarkImageAssets = mediaLibrary.filter(
    (asset) => asset.assetType === "images",
  );
  const benchmarkAudioAssets = mediaLibrary.filter(
    (asset) => asset.assetType === "audio",
  );
  const renderBenchmarkImageOptions = () =>
    benchmarkImageAssets.map((asset) => (
      <MenuItem key={asset.key} value={asset.publicUrl} sx={{ gap: 2 }}>
        <Box
          component="img"
          src={asset.publicUrl}
          alt={benchmarkAssetLabel(asset)}
          sx={{ width: 24, height: 24, objectFit: "contain" }}
        />
        {benchmarkAssetLabel(asset)}
      </MenuItem>
    ));
  const renderBenchmarkAudioOptions = () =>
    benchmarkAudioAssets.map((asset) => (
      <MenuItem key={asset.key} value={asset.publicUrl}>
        {benchmarkAssetLabel(asset)}
      </MenuItem>
    ));

  useEffect(() => {
    const params = new URLSearchParams(search);
    const pk = params.get("pk")?.trim();
    const benchmarkFromQuery = params.get("benchmark")?.trim();
    if (benchmarkFromQuery) {
      setBenchmark(benchmarkFromQuery);
    }
    const isDemoFromQuery = params.get("is_demo")?.trim();
    if (isDemoFromQuery) {
      setIsDemo(isDemoFromQuery);
    }
    if (!pk || !token || !apiUrl) {
      return;
    }

    const controller = new AbortController();

    const loadExistingTest = async () => {
      try {
        setLoadingExistingTest(true);
        setExistingTestError(null);

        const response = await fetch(
          `${apiUrl}/content/listening?pk=${encodeURIComponent(pk)}`,
          {
            method: "GET",
            headers: { Authorization: `Bearer ${token}` },
            signal: controller.signal,
          },
        );

        if (!response.ok) {
          setExistingTestError(`Unable to load test (${response.status})`);
          return;
        }

        const payload = (await response.json()) as TestDetailsResponse;
        const details = payload?.test;
        const items = Array.isArray(details?.items) ? details.items : [];
        const resolvedPK = details?.pk ?? pk;

        const metaItem =
          items.find((item) => asString(item.SK) === "META") ?? {};
        const partItems = items
          .filter(
            (item) =>
              asString(item.entity_type) === "test_part" ||
              asString(item.SK).startsWith("PART#"),
          )
          .sort((left, right) =>
            asString(left.SK).localeCompare(asString(right.SK)),
          );
        const questionItems = items.filter((item) => {
          const sk = asString(item.SK);
          return (
            sk.startsWith("QUESTION#") ||
            asString(item.entity_type) === "test_question"
          );
        });

        const normalizedMeta: ListeningAssessmentMeta = {
          difficultyLevel:
            asString(metaItem.difficulty_level) ||
            parseDifficultyLevelFromPK(resolvedPK),
          assessmentTitle: asString(
            metaItem.assessment_title ?? metaItem.assessmentTitle,
          ),
          overallInstructions: asString(
            metaItem.instructions ?? metaItem.overallInstructions,
          ),
          instructionsAudio: asString(
            metaItem.instructions_audio ?? metaItem.instructionsAudio,
          ),
          instructionImage: asString(
            metaItem.instruction_image ?? metaItem.instructionImage,
          ),
          yesNoInstructions: asString(
            metaItem.yes_no_instructions ?? metaItem.yesNoInstructions,
          ),
          yesNoInstructionsAudio: asString(
            metaItem.yes_no_instructions_audio ??
              metaItem.yesNoInstructionsAudio,
          ),
          yesNoQuestionAudio: asString(
            metaItem.yes_no_question_audio ?? metaItem.yesNoQuestionAudio,
          ),
          yesNoInstructionImage: asString(
            metaItem.yes_no_instruction_image ?? metaItem.yesNoInstructionImage,
          ),
          studentSelectionInstructions: asString(
            metaItem.student_selection_instructions ??
              metaItem.studentSelectionInstructions,
          ),
          studentSelectionInstructionsAudio: asString(
            metaItem.student_selection_instructions_audio ??
              metaItem.studentSelectionInstructionsAudio,
          ),
          studentSelectionQuestionAudio: asString(
            metaItem.student_selection_question_audio ??
              metaItem.studentSelectionQuestionAudio,
          ),
          studentSelectionInstructionImage: asString(
            metaItem.student_selection_instruction_image ??
              metaItem.studentSelectionInstructionImage,
          ),
          recapInstructions: asString(
            metaItem.recap_instructions ?? metaItem.recapInstructions,
          ),
          recapInstructionsAudio: asString(
            metaItem.recap_instructions_audio ??
              metaItem.recapInstructionsAudio,
          ),
          recapImage: asString(metaItem.recap_image ?? metaItem.recapImage),
          characterGroup: {
            teacher: {
              id:
                asString(
                  (
                    metaItem.character_group as
                      | Record<string, unknown>
                      | undefined
                  )?.teacher &&
                    (
                      (metaItem.character_group as Record<string, unknown>)
                        .teacher as Record<string, unknown>
                    ).id,
                ) || createLocalId(),
              name: asString(
                (
                  metaItem.character_group as
                    | Record<string, unknown>
                    | undefined
                )?.teacher &&
                  (
                    (metaItem.character_group as Record<string, unknown>)
                      .teacher as Record<string, unknown>
                  ).name,
              ),
              image: asString(
                (
                  metaItem.character_group as
                    | Record<string, unknown>
                    | undefined
                )?.teacher &&
                  (
                    (metaItem.character_group as Record<string, unknown>)
                      .teacher as Record<string, unknown>
                  ).image,
              ),
            },
            students: Array.isArray(
              (metaItem.character_group as Record<string, unknown> | undefined)
                ?.students,
            )
              ? (
                  (metaItem.character_group as Record<string, unknown>)
                    .students as Array<Record<string, unknown>>
                ).map((student) => ({
                  id: asString(student.id) || createLocalId(),
                  name: asString(student.name),
                  image: asString(student.image),
                }))
              : [createCharacter()],
          },
        };

        const rawMedia = Array.isArray(metaItem.media_library)
          ? (metaItem.media_library as Array<Record<string, unknown>>)
          : [];
        const imageFromMeta = Array.isArray(metaItem.image_library)
          ? (metaItem.image_library as Array<Record<string, unknown>>)
          : [];
        const audioFromMeta = Array.isArray(metaItem.audio_library)
          ? (metaItem.audio_library as Array<Record<string, unknown>>)
          : [];

        const normalizedImages = (
          imageFromMeta.length > 0 ? imageFromMeta : rawMedia
        )
          .map((item): ImageMediaItem | null => {
            const imageUrl = asString(
              item.imageUrl ?? item.image_url ?? item.url,
            );
            if (!imageUrl) {
              return null;
            }
            return {
              id: asString(item.id) || createLocalId(),
              label: asString(item.label ?? item.title),
              imageUrl,
              altText: asString(item.altText ?? item.alt_text),
            };
          })
          .filter((item): item is ImageMediaItem => item !== null);

        const normalizedAudio = (
          audioFromMeta.length > 0 ? audioFromMeta : rawMedia
        )
          .map((item): AudioMediaItem | null => {
            const audioUrl = asString(
              item.audioUrl ?? item.audio_url ?? item.url,
            );
            if (!audioUrl) {
              return null;
            }
            return {
              id: asString(item.id) || createLocalId(),
              label: asString(item.label ?? item.title),
              audioUrl,
            };
          })
          .filter((item): item is AudioMediaItem => item !== null);

        const nextParts: ListeningPart[] = createInitialParts().map(
          (basePart, index) => {
            const partItem = partItems[index] ?? {};
            return {
              ...basePart,
              title: asString(partItem.title) || `Part ${index + 1}`,
              promptImage: asString(
                partItem.prompt_image ?? partItem.promptImage,
              ),
              promptAudio: asString(
                partItem.prompt_audio ?? partItem.promptAudio,
              ),
              yesNoBank: [] as YesNoQuestion[],
              studentSelectBank: [] as StudentSelectQuestion[],
            };
          },
        );

        for (const item of questionItems) {
          const partIndex = partIndexFromQuestion(item);
          const questionType = asString(item.question_type).toLowerCase();
          const part = nextParts[partIndex];
          if (!part) {
            continue;
          }

          if (questionType === "yes_no") {
            const correctOptionRaw = asString(
              item.correct_option ?? item.correctOption,
            ).toUpperCase();
            part.yesNoBank.push({
              id: asString(item.question_id) || createLocalId(),
              image: asString(item.instruction_image ?? item.image),
              audio: asString(item.audio_media ?? item.audio),
              prompt: asString(item.prompt),
              optionA: asString(item.A ?? item.optionA),
              optionB: asString(item.B ?? item.optionB),
              correctOption: correctOptionRaw === "B" ? "B" : "A",
            });
          }

          if (questionType === "student_select") {
            const rawOptions = Array.isArray(item.options)
              ? (item.options as Array<Record<string, unknown>>)
              : [];
            part.studentSelectBank.push({
              id: asString(item.question_id) || createLocalId(),
              image: asString(item.instruction_image ?? item.image),
              prompt: asString(item.prompt),
              promptAudio: asString(item.prompt_audio ?? item.promptAudio),
              correctOption: asString(
                item.correct_option ?? item.correctOption,
              ),
              options:
                rawOptions.length > 0
                  ? rawOptions.map((option) => ({
                      id: asString(option.id) || createLocalId(),
                      studentId: asString(
                        option.studentId ?? option.student_id,
                      ),
                      audio: asString(option.audio),
                    }))
                  : [createStudentSelectOption()],
            });
          }
        }

        nextParts.forEach((part) => {
          if (part.yesNoBank.length === 0) {
            part.yesNoBank = [createYesNoQuestion()];
          }
          if (part.studentSelectBank.length === 0) {
            part.studentSelectBank = [createStudentSelectQuestion()];
          }
        });

        setMeta(normalizedMeta);
        setImageLibrary(
          normalizedImages.length > 0
            ? normalizedImages
            : [createImageMediaItem()],
        );
        setAudioLibrary(
          normalizedAudio.length > 0
            ? normalizedAudio
            : [createAudioMediaItem()],
        );
        setParts(nextParts);
        setCollapsedYesNoQuestions({});
        setCollapsedStudentSelectQuestions({});
        setDefaultCollapsedAppliedByPart({});
        setActiveTab("meta");
        setBenchmark(
          asString(metaItem.benchmark) ||
            parseBenchmarkFromPK(resolvedPK) ||
            benchmarkFromQuery || ""
        );
      } catch (loadError: unknown) {
        if (
          loadError instanceof DOMException &&
          loadError.name === "AbortError"
        ) {
          return;
        }
        setExistingTestError(
          loadError instanceof Error
            ? loadError.message
            : "Unable to load test details.",
        );
      } finally {
        setLoadingExistingTest(false);
      }
    };

    loadExistingTest().then();
    return () => controller.abort();
  }, [apiUrl, search, setBenchmark, setIsDemo, token]);

  useEffect(() => {
    if (!canListBenchmarkAssets) {
      return;
    }

    const controller = new AbortController();
    listAssessmentBenchmarkAssetsRequest({
      apiUrl,
      token,
      benchmarkId: benchmark,
      isDemo: isDemo,
      signal: controller.signal,
    })
      .then((assets) => {
        setBenchmarkAssets(assets);
        setBenchmarkAssetsError(null);
      })
      .catch((error: unknown) => {
        if (error instanceof DOMException && error.name === "AbortError") {
          return;
        }
        setBenchmarkAssets([]);
        setBenchmarkAssetsError(
          error instanceof Error
            ? error.message
            : "Unable to load benchmark assets.",
        );
      });

    return () => controller.abort();
  }, [
    apiUrl,
    benchmark,
    benchmarkAssetsRefreshKey,
    canListBenchmarkAssets,
    token,
    isDemo,
  ]);

  const yesNoQuestionKey = (partIndex: number, questionId: string): string =>
    `${partIndex}::yes-no::${questionId}`;
  const studentSelectQuestionKey = (
    partIndex: number,
    questionId: string,
  ): string => `${partIndex}::student-select::${questionId}`;
  const yesNoPreviewKey = (partIndex: number, questionId: string): string =>
    `${partIndex}::yes-no-preview::${questionId}`;
  const studentSelectPreviewKey = (
    partIndex: number,
    questionId: string,
  ): string => `${partIndex}::student-select-preview::${questionId}`;

  const isYesNoCollapsed = (partIndex: number, questionId: string): boolean =>
    collapsedYesNoQuestions[yesNoQuestionKey(partIndex, questionId)] || false;

  const isStudentSelectCollapsed = (
    partIndex: number,
    questionId: string,
  ): boolean =>
    collapsedStudentSelectQuestions[
      studentSelectQuestionKey(partIndex, questionId)
    ] || false;

  const isYesNoPreviewVisible = (
    partIndex: number,
    questionId: string,
  ): boolean =>
    visibleYesNoPreviews[yesNoPreviewKey(partIndex, questionId)] ?? false;

  const isStudentSelectPreviewVisible = (
    partIndex: number,
    questionId: string,
  ): boolean =>
    visibleStudentSelectPreviews[
      studentSelectPreviewKey(partIndex, questionId)
    ] ?? false;

  const toggleYesNoCollapsed = (partIndex: number, questionId: string) => {
    const key = yesNoQuestionKey(partIndex, questionId);
    setCollapsedYesNoQuestions((current) => ({
      ...current,
      [key]: !current[key],
    }));
  };

  const toggleStudentSelectCollapsed = (
    partIndex: number,
    questionId: string,
  ) => {
    const key = studentSelectQuestionKey(partIndex, questionId);
    setCollapsedStudentSelectQuestions((current) => ({
      ...current,
      [key]: !current[key],
    }));
  };

  const toggleYesNoPreview = (partIndex: number, questionId: string) => {
    const key = yesNoPreviewKey(partIndex, questionId);
    setVisibleYesNoPreviews((current) => ({
      ...current,
      [key]: !current[key],
    }));
  };

  const toggleStudentSelectPreview = (
    partIndex: number,
    questionId: string,
  ) => {
    const key = studentSelectPreviewKey(partIndex, questionId);
    setVisibleStudentSelectPreviews((current) => ({
      ...current,
      [key]: !current[key],
    }));
  };

  const handleTabChange = (_event: SyntheticEvent, value: string) => {
    if (value.startsWith("part-")) {
      const partIndex = Number(value.replace("part-", ""));
      const part = parts[partIndex];
      if (
        !Number.isNaN(partIndex) &&
        part &&
        !defaultCollapsedAppliedByPart[partIndex]
      ) {
        setCollapsedYesNoQuestions((current) => {
          const next = { ...current };
          part.yesNoBank.forEach((question) => {
            next[yesNoQuestionKey(partIndex, question.id)] = true;
          });
          return next;
        });

        setCollapsedStudentSelectQuestions((current) => {
          const next = { ...current };
          part.studentSelectBank.forEach((question) => {
            next[studentSelectQuestionKey(partIndex, question.id)] = true;
          });
          return next;
        });

        setDefaultCollapsedAppliedByPart((current) => ({
          ...current,
          [partIndex]: true,
        }));
      }
    }

    setActiveTab(value);
  };

  const updateMeta = (field: keyof ListeningAssessmentMeta, value: string) => {
    setMeta((current) => ({ ...current, [field]: value }));
  };

  const updateTeacher = (field: "name" | "image", value: string) => {
    setMeta((current) => ({
      ...current,
      characterGroup: {
        ...current.characterGroup,
        teacher: {
          ...current.characterGroup.teacher,
          [field]: value,
        },
      },
    }));
  };

  const addStudent = () => {
    setMeta((current) => ({
      ...current,
      characterGroup: {
        ...current.characterGroup,
        students: [...current.characterGroup.students, createCharacter()],
      },
    }));
    setIsCharacterGroupCollapsed(false);
  };

  const updateStudent = (
    studentId: string,
    field: "name" | "image",
    value: string,
  ) => {
    setMeta((current) => ({
      ...current,
      characterGroup: {
        ...current.characterGroup,
        students: current.characterGroup.students.map((student) =>
          student.id === studentId ? { ...student, [field]: value } : student,
        ),
      },
    }));
  };

  const removeStudent = (studentId: string) => {
    setMeta((current) => ({
      ...current,
      characterGroup: {
        ...current.characterGroup,
        students: current.characterGroup.students.filter(
          (student) => student.id !== studentId,
        ),
      },
    }));

    setParts((current) =>
      current.map((part) => ({
        ...part,
        studentSelectBank: part.studentSelectBank.map((question) => ({
          ...question,
          correctOption:
            question.correctOption === studentId ? "" : question.correctOption,
          options: question.options.map((option) =>
            option.studentId === studentId
              ? { ...option, studentId: "" }
              : option,
          ),
        })),
      })),
    );
  };

  const updatePart = (
    partIndex: number,
    updater: (part: ListeningPart) => ListeningPart,
  ) => {
    setParts((current) =>
      current.map((part, index) =>
        index === partIndex ? updater(part) : part,
      ),
    );
  };

  const setAllYesNoCollapsedForPart = (
    partIndex: number,
    collapsed: boolean,
  ) => {
    const part = parts[partIndex];
    if (!part) {
      return;
    }
    setCollapsedYesNoQuestions((current) => {
      const next = { ...current };
      part.yesNoBank.forEach((question) => {
        next[yesNoQuestionKey(partIndex, question.id)] = collapsed;
      });
      return next;
    });
  };

  const setAllStudentSelectCollapsedForPart = (
    partIndex: number,
    collapsed: boolean,
  ) => {
    const part = parts[partIndex];
    if (!part) {
      return;
    }
    setCollapsedStudentSelectQuestions((current) => {
      const next = { ...current };
      part.studentSelectBank.forEach((question) => {
        next[studentSelectQuestionKey(partIndex, question.id)] = collapsed;
      });
      return next;
    });
  };

  const updatePartTitle = (partIndex: number, value: string) => {
    updatePart(partIndex, (part) => ({ ...part, title: value }));
  };

  const addYesNoQuestion = (partIndex: number) => {
    updatePart(partIndex, (part) => ({
      ...part,
      yesNoBank: [...part.yesNoBank, createYesNoQuestion()],
    }));
  };

  const updateYesNoQuestion = (
    partIndex: number,
    questionId: string,
    field: keyof YesNoQuestion,
    value: string,
  ) => {
    updatePart(partIndex, (part) => ({
      ...part,
      yesNoBank: part.yesNoBank.map((question) =>
        question.id === questionId ? { ...question, [field]: value } : question,
      ),
    }));
  };

  const removeYesNoQuestion = (partIndex: number, questionId: string) => {
    const key = yesNoQuestionKey(partIndex, questionId);
    setCollapsedYesNoQuestions((current) => {
      const next = { ...current };
      delete next[key];
      return next;
    });
    updatePart(partIndex, (part) => ({
      ...part,
      yesNoBank: part.yesNoBank.filter(
        (question) => question.id !== questionId,
      ),
    }));
  };

  const addStudentSelectQuestion = (partIndex: number) => {
    updatePart(partIndex, (part) => ({
      ...part,
      studentSelectBank: [
        ...part.studentSelectBank,
        createStudentSelectQuestion(),
      ],
    }));
  };

  const updateStudentSelectQuestion = (
    partIndex: number,
    questionId: string,
    field: keyof StudentSelectQuestion,
    value: string,
  ) => {
    updatePart(partIndex, (part) => ({
      ...part,
      studentSelectBank: part.studentSelectBank.map((question) =>
        question.id === questionId ? { ...question, [field]: value } : question,
      ),
    }));
  };

  const removeStudentSelectQuestion = (
    partIndex: number,
    questionId: string,
  ) => {
    const key = studentSelectQuestionKey(partIndex, questionId);
    setCollapsedStudentSelectQuestions((current) => {
      const next = { ...current };
      delete next[key];
      return next;
    });
    updatePart(partIndex, (part) => ({
      ...part,
      studentSelectBank: part.studentSelectBank.filter(
        (question) => question.id !== questionId,
      ),
    }));
  };

  const addStudentSelectOption = (partIndex: number, questionId: string) => {
    updatePart(partIndex, (part) => ({
      ...part,
      studentSelectBank: part.studentSelectBank.map((question) =>
        question.id === questionId
          ? {
              ...question,
              options: [...question.options, createStudentSelectOption()],
            }
          : question,
      ),
    }));
  };

  const updateStudentSelectOption = (
    partIndex: number,
    questionId: string,
    optionId: string,
    field: keyof StudentSelectOption,
    value: string,
  ) => {
    updatePart(partIndex, (part) => ({
      ...part,
      studentSelectBank: part.studentSelectBank.map((question) =>
        question.id === questionId
          ? (() => {
              const nextOptions = question.options.map((option) =>
                option.id === optionId ? { ...option, [field]: value } : option,
              );
              const hasCurrentCorrectOption = nextOptions.some(
                (option) => option.studentId === question.correctOption,
              );
              return {
                ...question,
                correctOption: hasCurrentCorrectOption
                  ? question.correctOption
                  : "",
                options: nextOptions,
              };
            })()
          : question,
      ),
    }));
  };

  const removeStudentSelectOption = (
    partIndex: number,
    questionId: string,
    optionId: string,
  ) => {
    updatePart(partIndex, (part) => ({
      ...part,
      studentSelectBank: part.studentSelectBank.map((question) =>
        question.id === questionId
          ? (() => {
              const optionToRemove = question.options.find(
                (option) => option.id === optionId,
              );
              const nextCorrectOption =
                optionToRemove &&
                optionToRemove.studentId === question.correctOption
                  ? ""
                  : question.correctOption;
              return {
                ...question,
                correctOption: nextCorrectOption,
                options: question.options.filter(
                  (option) => option.id !== optionId,
                ),
              };
            })()
          : question,
      ),
    }));
  };

  const handleExportTest = () => {
    const payload: ListeningTestTransferFile = {
      version: "1",
      exportedAt: new Date().toISOString(),
      test: {
        pk: existingTestPk || undefined,
        difficulty_level: meta.difficultyLevel,
        test_type: "listening",
        status: "draft",
        benchmark: benchmark.trim(),
        meta: toListeningSaveMeta(meta),
        parts: parts.map((part, index) => ({
          part: index + 1,
          title: part.title,
          prompt_image: part.promptImage,
          prompt_audio: part.promptAudio,
          questions: {
            yes_no: part.yesNoBank,
            student_select: part.studentSelectBank,
          },
        })),
      },
    };
    const safeGrade = meta.difficultyLevel.trim() || "unknown-difficultyLevel";
    const filename = `listening-test-${safeGrade}-${new Date().toISOString().replaceAll(":", "-")}.json`;
    const blob = new Blob([JSON.stringify(payload, null, 2)], {
      type: "application/json",
    });
    const objectUrl = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = objectUrl;
    link.download = filename;
    link.click();
    URL.revokeObjectURL(objectUrl);
  };

  const handleImportClick = () => {
    setMoreMenuAnchorEl(null);
    setImportError(null);
    importInputRef.current?.click();
  };

  const handleOpenMoreMenu = (event: MouseEvent<HTMLElement>) => {
    setMoreMenuAnchorEl(event.currentTarget);
  };

  const handleCloseMoreMenu = () => {
    setMoreMenuAnchorEl(null);
  };

  const updateYesNoSearch = (partIndex: number, value: string) => {
    setYesNoSearchByPart((current) => ({ ...current, [partIndex]: value }));
  };

  const updateStudentSelectSearch = (partIndex: number, value: string) => {
    setStudentSelectSearchByPart((current) => ({
      ...current,
      [partIndex]: value,
    }));
  };

  const handleImportFile = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) {
      return;
    }

    try {
      const fileContent = await file.text();
      const parsed = JSON.parse(fileContent) as ListeningTestTransferFile;
      const test = parsed?.test;
      if (!test || !Array.isArray(test.parts)) {
        setImportMessage(null);
        setImportError("Invalid import file format.");
        return;
      }

      const importedPk = asString(test.pk).trim();
      const importedBenchmark = asString(test.benchmark).trim();
      const importedMetaRaw = (test.meta ?? {}) as Record<string, unknown>;
      const importedCharacterGroup = (importedMetaRaw.characterGroup ??
        importedMetaRaw.character_group ??
        {}) as Record<string, unknown>;
      const importedTeacher = (importedCharacterGroup.teacher ?? {}) as Record<
        string,
        unknown
      >;
      const importedStudents = Array.isArray(importedCharacterGroup.students)
        ? (importedCharacterGroup.students as Array<Record<string, unknown>>)
        : [];

      const normalizedMeta: ListeningAssessmentMeta = {
        difficultyLevel:
          asString(test.difficulty_level) ||
          parseDifficultyLevelFromPK(importedPk),
        assessmentTitle: asString(
          importedMetaRaw.assessmentTitle ?? importedMetaRaw.assessment_title,
        ),
        overallInstructions: asString(
          importedMetaRaw.overallInstructions ?? importedMetaRaw.instructions,
        ),
        instructionsAudio: asString(
          importedMetaRaw.instructionsAudio ??
            importedMetaRaw.instructions_audio,
        ),
        instructionImage: asString(
          importedMetaRaw.instructionImage ?? importedMetaRaw.instruction_image,
        ),
        yesNoInstructions: asString(
          importedMetaRaw.yesNoInstructions ??
            importedMetaRaw.yes_no_instructions,
        ),
        yesNoInstructionsAudio: asString(
          importedMetaRaw.yesNoInstructionsAudio ??
            importedMetaRaw.yes_no_instructions_audio,
        ),
        yesNoQuestionAudio: asString(
          importedMetaRaw.yesNoQuestionAudio ??
            importedMetaRaw.yes_no_question_audio,
        ),
        yesNoInstructionImage: asString(
          importedMetaRaw.yesNoInstructionImage ??
            importedMetaRaw.yes_no_instruction_image,
        ),
        studentSelectionInstructions: asString(
          importedMetaRaw.studentSelectionInstructions ??
            importedMetaRaw.student_selection_instructions,
        ),
        studentSelectionInstructionsAudio: asString(
          importedMetaRaw.studentSelectionInstructionsAudio ??
            importedMetaRaw.student_selection_instructions_audio,
        ),
        studentSelectionQuestionAudio: asString(
          importedMetaRaw.studentSelectionQuestionAudio ??
            importedMetaRaw.student_selection_question_audio,
        ),
        studentSelectionInstructionImage: asString(
          importedMetaRaw.studentSelectionInstructionImage ??
            importedMetaRaw.student_selection_instruction_image,
        ),
        recapInstructions: asString(
          importedMetaRaw.recapInstructions ??
            importedMetaRaw.recap_instructions,
        ),
        recapInstructionsAudio: asString(
          importedMetaRaw.recapInstructionsAudio ??
            importedMetaRaw.recap_instructions_audio,
        ),
        recapImage: asString(
          importedMetaRaw.recapImage ?? importedMetaRaw.recap_image,
        ),
        characterGroup: {
          teacher: {
            id: asString(importedTeacher.id) || createLocalId(),
            name: asString(importedTeacher.name),
            image: asString(importedTeacher.image),
          },
          students:
            importedStudents.length > 0
              ? importedStudents.map((student) => ({
                  id: asString(student.id) || createLocalId(),
                  name: asString(student.name),
                  image: asString(student.image),
                }))
              : [createCharacter()],
        },
      };

      const importedParts = test.parts as Array<Record<string, unknown>>;
      const normalizedParts = createInitialParts().map((basePart, index) => {
        const importedPart = importedParts[index] ?? {};
        const questions = (importedPart.questions ??
          importedPart.question_banks ??
          {}) as Record<string, unknown>;
        const nestedQuestions = (questions.questions ?? {}) as Record<
          string,
          unknown
        >;
        const importedYesNo = Array.isArray(questions.yes_no)
          ? questions.yes_no
          : Array.isArray(nestedQuestions.yes_no)
            ? nestedQuestions.yes_no
            : [];
        const importedStudentSelect = Array.isArray(questions.student_select)
          ? questions.student_select
          : Array.isArray(nestedQuestions.student_select)
            ? nestedQuestions.student_select
            : [];

        return {
          ...basePart,
          title: asString(importedPart.title) || `Part ${index + 1}`,
          promptImage: asString(
            importedPart.prompt_image ?? importedPart.promptImage,
          ),
          promptAudio: asString(
            importedPart.prompt_audio ?? importedPart.promptAudio,
          ),
          yesNoBank:
            importedYesNo.length > 0
              ? importedYesNo.map((raw): YesNoQuestion => {
                  const item = raw as Record<string, unknown>;
                  const correctOptionRaw = asString(
                    item.correctOption ?? item.correct_option,
                  ).toUpperCase();
                  return {
                    id: asString(item.id) || createLocalId(),
                    image: asString(item.image),
                    audio: asString(item.audio),
                    prompt: asString(item.prompt),
                    optionA: asString(item.optionA ?? item.A),
                    optionB: asString(item.optionB ?? item.B),
                    correctOption: correctOptionRaw === "B" ? "B" : "A",
                  };
                })
              : [createYesNoQuestion()],
          studentSelectBank:
            importedStudentSelect.length > 0
              ? importedStudentSelect.map((raw): StudentSelectQuestion => {
                  const item = raw as Record<string, unknown>;
                  const optionsRaw = Array.isArray(item.options)
                    ? item.options
                    : [];
                  return {
                    id: asString(item.id) || createLocalId(),
                    image: asString(item.image),
                    prompt: asString(item.prompt),
                    promptAudio: asString(
                      item.promptAudio ?? item.prompt_audio,
                    ),
                    correctOption: asString(
                      item.correct_option ?? item.correctOption,
                    ),
                    options:
                      optionsRaw.length > 0
                        ? optionsRaw.map((optionRaw): StudentSelectOption => {
                            const option = optionRaw as Record<string, unknown>;
                            return {
                              id: asString(option.id) || createLocalId(),
                              studentId: asString(
                                option.studentId ?? option.student_id,
                              ),
                              audio: asString(option.audio),
                            };
                          })
                        : [createStudentSelectOption()],
                  };
                })
              : [createStudentSelectQuestion()],
        };
      });

      setMeta(normalizedMeta);
      setParts(normalizedParts);
      setCollapsedYesNoQuestions({});
      setCollapsedStudentSelectQuestions({});
      setDefaultCollapsedAppliedByPart({});
      setActiveTab("meta");
      setBenchmark(
        parseBenchmarkFromPK(importedPk) ||
          importedBenchmark ||
          benchmark
      );

      if (importedPk) {
        setExistingTestPk(importedPk);
        const currentUrl = new URL(window.location.href);
        currentUrl.searchParams.set("pk", importedPk);
        currentUrl.searchParams.set(
          "benchmark",
          parseBenchmarkFromPK(importedPk) ||
            importedBenchmark ||
            benchmark
        );
        window.history.replaceState(
          {},
          "",
          `${currentUrl.pathname}${currentUrl.search}${currentUrl.hash}`,
        );
      } else {
        setExistingTestPk("");
        const currentUrl = new URL(window.location.href);
        currentUrl.searchParams.delete("pk");
        currentUrl.searchParams.set(
          "benchmark",
          importedBenchmark || benchmark,
        );
        window.history.replaceState(
          {},
          "",
          `${currentUrl.pathname}${currentUrl.search}${currentUrl.hash}`,
        );
      }

      setImportError(null);
      setImportMessage(
        `Imported ${file.name}. Review and click Save Test to write to this environment.`,
      );
      setSaveError(null);
    } catch (importTestError: unknown) {
      setImportMessage(null);
      setImportError(
        importTestError instanceof Error
          ? importTestError.message
          : "Unable to import JSON file.",
      );
    } finally {
      event.target.value = "";
    }
  };

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "s") {
        event.preventDefault();
        if (!isSavingTest) {
          void saveTest();
        }
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isSavingTest, saveTest]);

  const manageTestsUrl = benchmark.trim()
    ? `/assessments-admin/benchmarks/manage?benchmark=${encodeURIComponent(benchmark.trim())}${isDemo ? `&is_demo=${encodeURIComponent(isDemo)}` : ""}`
    : "/assessments-admin/benchmarks/manage";
  const isMoreMenuOpen = Boolean(moreMenuAnchorEl);
  const compactButtonSx = {
    px: 1.25,
    py: 0.5,
    minWidth: "auto",
  };
  const compactEditorFieldSx = {
    "& .MuiButton-root": compactButtonSx,
    "& .MuiInputBase-root": {
      minHeight: 40,
    },
    "& .MuiOutlinedInput-input": {
      paddingTop: "8.5px",
      paddingBottom: "8.5px",
      lineHeight: 1.25,
    },
    "& .MuiInputBase-inputMultiline": {
      paddingTop: 0,
      paddingBottom: 0,
    },
    "& .MuiOutlinedInput-root.MuiInputBase-multiline": {
      alignItems: "flex-start",
      py: 1,
    },
  };

  return (
    <Container
      maxWidth="xl"
      sx={{
        py: 3,
        pb: 14,
        ...compactEditorFieldSx,
      }}
    >
      <Stack spacing={2.5}>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="flex-start"
          spacing={2}
        >
          <Box>
            <Link
              component={RouterLink}
              to={manageTestsUrl}
              underline="hover"
              sx={{ alignSelf: "flex-start", fontSize: 14 }}
            >
              Back to dashboard
            </Link>
            <Typography variant="h4">Listening Assessment Builder</Typography>
          </Box>
          <IconButton
            aria-label="More actions"
            aria-controls={
              isMoreMenuOpen ? "listening-builder-actions" : undefined
            }
            aria-haspopup="true"
            aria-expanded={isMoreMenuOpen ? "true" : undefined}
            onClick={handleOpenMoreMenu}
          >
            <MoreVertIcon />
          </IconButton>
        </Stack>
        <Menu
          id="listening-builder-actions"
          anchorEl={moreMenuAnchorEl}
          open={isMoreMenuOpen}
          onClose={handleCloseMoreMenu}
        >
          <MenuItem
            onClick={() => {
              handleCloseMoreMenu();
              handleExportTest();
            }}
          >
            Export JSON
          </MenuItem>
          <MenuItem onClick={handleImportClick}>Import JSON</MenuItem>
          <MenuItem
            onClick={() => {
              handleCloseMoreMenu();
              setIsAssetUploadDialogOpen(true);
            }}
          >
            Upload to S3
          </MenuItem>
        </Menu>
        <BenchmarkAssetUploadDialog
          open={isAssetUploadDialogOpen}
          apiUrl={apiUrl}
          token={token}
          benchmarkId={benchmark}
          onClose={() => setIsAssetUploadDialogOpen(false)}
          onUploadComplete={() =>
            setBenchmarkAssetsRefreshKey((current) => current + 1)
          }
          isDemo={isDemo ? JSON.parse(isDemo) : false}
        />
        <Typography variant="body2" color="text.secondary">
          Build listening test content. This initial step sets up metadata and
          media used across the test.
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Benchmark: <Box component="code">{benchmark}</Box>
        </Typography>
        {existingTestPk ? (
          <Typography variant="body2" color="text.secondary">
            Editing test: <Box component="code">{existingTestPk}</Box>
          </Typography>
        ) : null}

        <input
          ref={importInputRef}
          type="file"
          accept="application/json,.json"
          style={{ display: "none" }}
          onChange={handleImportFile}
        />

        {saveMessage ? <Alert severity="success">{saveMessage}</Alert> : null}
        {saveError ? <Alert severity="error">{saveError}</Alert> : null}
        {benchmarkAssetsError ? (
          <Alert severity="error">{benchmarkAssetsError}</Alert>
        ) : null}
        {importMessage ? <Alert severity="info">{importMessage}</Alert> : null}
        {importError ? <Alert severity="error">{importError}</Alert> : null}
        {loadingExistingTest ? (
          <Typography variant="body2" color="text.secondary">
            Loading existing test data...
          </Typography>
        ) : null}
        {existingTestError ? (
          <Alert severity="error">{existingTestError}</Alert>
        ) : null}

        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          variant="scrollable"
          allowScrollButtonsMobile
          aria-label="Listening assessment sections"
        >
          <Tab value="meta" label="Meta" />
          {parts.map((part, partIndex) => {
            const tabId = `part-${partIndex}`;
            return (
              <Tab
                key={tabId}
                value={tabId}
                label={part.title || `Part ${partIndex + 1}`}
              />
            );
          })}
        </Tabs>

        {activeTab === "meta" ? (
          <Paper
            variant="outlined"
            sx={{ p: 2.5, "& .MuiTextField-root": { minWidth: 0 } }}
          >
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xs: "1fr",
                  md: "repeat(2, minmax(0, 1fr))",
                  lg: "repeat(3, minmax(0, 1fr))",
                },
                gap: 2,
              }}
            >
              <TextField
                select
                label="Difficulty Level"
                value={meta.difficultyLevel}
                onChange={(event) =>
                  updateMeta("difficultyLevel", event.target.value)
                }
                fullWidth
              >
                <MenuItem value="">Select difficulty level</MenuItem>
                {difficultyLevelOptions.map((difficultyLevelOption) => (
                  <MenuItem
                    key={difficultyLevelOption.value}
                    value={difficultyLevelOption.value}
                  >
                    {difficultyLevelOption.label}
                  </MenuItem>
                ))}
              </TextField>

              <TextField
                label="Assessment Title"
                value={meta.assessmentTitle}
                onChange={(event) =>
                  updateMeta("assessmentTitle", event.target.value)
                }
                fullWidth
              />

              <Box aria-hidden sx={{ display: { xs: "none" } }} />

              <TextField
                label="Overall Test Instructions"
                value={meta.overallInstructions}
                onChange={(event) =>
                  updateMeta("overallInstructions", event.target.value)
                }
                multiline
                rows={3}
                fullWidth
              />

              <TextField
                select
                label="Instruction Audio"
                value={meta.instructionsAudio}
                onChange={(event) =>
                  updateMeta("instructionsAudio", event.target.value)
                }
                fullWidth
              >
                <MenuItem value="">None</MenuItem>
                {renderBenchmarkAudioOptions()}
              </TextField>

              <TextField
                select
                label="Test Instructions Image"
                value={meta.instructionImage}
                onChange={(event) =>
                  updateMeta("instructionImage", event.target.value)
                }
                fullWidth
              >
                <MenuItem value="">None</MenuItem>
                {renderBenchmarkImageOptions()}
              </TextField>

              <Box aria-hidden sx={{ display: { xs: "none", md: "block" } }} />

              <TextField
                label="Yes/No Question Instructions"
                value={meta.yesNoInstructions}
                onChange={(event) =>
                  updateMeta("yesNoInstructions", event.target.value)
                }
                multiline
                rows={2}
                fullWidth
              />

              <TextField
                select
                label="Yes/No Instructions Audio"
                value={meta.yesNoInstructionsAudio}
                onChange={(event) =>
                  updateMeta("yesNoInstructionsAudio", event.target.value)
                }
                fullWidth
              >
                <MenuItem value="">None</MenuItem>
                {renderBenchmarkAudioOptions()}
              </TextField>

              <TextField
                select
                label="Yes/No Question Audio"
                value={meta.yesNoQuestionAudio}
                onChange={(event) =>
                  updateMeta("yesNoQuestionAudio", event.target.value)
                }
                fullWidth
              >
                <MenuItem value="">None</MenuItem>
                {renderBenchmarkAudioOptions()}
              </TextField>

              <TextField
                select
                label="Yes/No Instructions Image"
                value={meta.yesNoInstructionImage}
                onChange={(event) =>
                  updateMeta("yesNoInstructionImage", event.target.value)
                }
                fullWidth
              >
                <MenuItem value="">None</MenuItem>
                {renderBenchmarkImageOptions()}
              </TextField>

              <Box aria-hidden sx={{ display: { xs: "none", md: "block" } }} />

              <TextField
                label="Student Selection Question Instructions"
                value={meta.studentSelectionInstructions}
                onChange={(event) =>
                  updateMeta("studentSelectionInstructions", event.target.value)
                }
                multiline
                rows={2}
                fullWidth
              />

              <TextField
                select
                label="Student Selection Instructions Audio"
                value={meta.studentSelectionInstructionsAudio}
                onChange={(event) =>
                  updateMeta(
                    "studentSelectionInstructionsAudio",
                    event.target.value,
                  )
                }
                fullWidth
              >
                <MenuItem value="">None</MenuItem>
                {renderBenchmarkAudioOptions()}
              </TextField>

              <TextField
                select
                label="Student Selection Question Audio"
                value={meta.studentSelectionQuestionAudio}
                onChange={(event) =>
                  updateMeta("studentSelectionQuestionAudio", event.target.value)
                }
                fullWidth
              >
                <MenuItem value="">None</MenuItem>
                {renderBenchmarkAudioOptions()}
              </TextField>

              <TextField
                select
                label="Student Selection Instructions Image"
                value={meta.studentSelectionInstructionImage}
                onChange={(event) =>
                  updateMeta(
                    "studentSelectionInstructionImage",
                    event.target.value,
                  )
                }
                fullWidth
              >
                <MenuItem value="">None</MenuItem>
                {renderBenchmarkImageOptions()}
              </TextField>

              <Box aria-hidden sx={{ display: { xs: "none", md: "block" } }} />

              <TextField
                label="Recap Instructions"
                value={meta.recapInstructions}
                onChange={(event) =>
                  updateMeta("recapInstructions", event.target.value)
                }
                multiline
                rows={2}
                fullWidth
              />

              <TextField
                select
                label="Recap Instructions Audio"
                value={meta.recapInstructionsAudio}
                onChange={(event) =>
                  updateMeta("recapInstructionsAudio", event.target.value)
                }
                fullWidth
              >
                <MenuItem value="">None</MenuItem>
                {renderBenchmarkAudioOptions()}
              </TextField>

              <TextField
                select
                label="Recap Image"
                value={meta.recapImage}
                onChange={(event) =>
                  updateMeta("recapImage", event.target.value)
                }
                fullWidth
              >
                <MenuItem value="">None</MenuItem>
                {renderBenchmarkImageOptions()}
              </TextField>
            </Box>

            <Stack spacing={2} sx={{ mt: 3 }}>
              <Stack
                direction={{ xs: "column", sm: "row" }}
                alignItems={{ xs: "flex-start", sm: "center" }}
                justifyContent="space-between"
                spacing={1}
              >
                <Typography variant="h6">Character Group</Typography>
                <Button
                  type="button"
                  size="medium"
                  variant="outlined"
                  onClick={() =>
                    setIsCharacterGroupCollapsed((current) => !current)
                  }
                >
                  {isCharacterGroupCollapsed ? "Expand" : "Collapse"}
                </Button>
              </Stack>

              {isCharacterGroupCollapsed ? (
                <Paper variant="outlined" sx={{ p: 2 }}>
                  <Typography variant="body2" color="text.secondary">
                    Teacher: {meta.characterGroup.teacher.name || "-"} |
                    Students: {meta.characterGroup.students.length}
                  </Typography>
                </Paper>
              ) : (
                <>
                  <Paper variant="outlined" sx={{ p: 2 }}>
                    <Typography variant="subtitle1" sx={{ mb: 2 }}>
                      Teacher
                    </Typography>
                    <Box
                      sx={{
                        display: "grid",
                        gridTemplateColumns: {
                          xs: "1fr",
                          md: "repeat(2, minmax(0, 1fr))",
                        },
                        gap: 2,
                      }}
                    >
                      <TextField
                        label="Teacher Name"
                        value={meta.characterGroup.teacher.name}
                        onChange={(event) =>
                          updateTeacher("name", event.target.value)
                        }
                        fullWidth
                      />
                      <TextField
                        select
                        label="Teacher Image"
                        value={meta.characterGroup.teacher.image}
                        onChange={(event) =>
                          updateTeacher("image", event.target.value)
                        }
                        fullWidth
                      >
                        <MenuItem value="">None</MenuItem>
                        {renderBenchmarkImageOptions()}
                      </TextField>
                    </Box>
                  </Paper>

                  <Stack
                    direction={{ xs: "column", sm: "row" }}
                    alignItems={{ xs: "flex-start", sm: "center" }}
                    justifyContent="space-between"
                    spacing={1}
                  >
                    <Typography variant="subtitle1">Students</Typography>
                    <Button
                      type="button"
                      size="medium"
                      variant="outlined"
                      onClick={addStudent}
                    >
                      Add Student
                    </Button>
                  </Stack>

                  {meta.characterGroup.students.map((student, index) => (
                    <Paper key={student.id} variant="outlined" sx={{ p: 2 }}>
                      <Stack
                        direction={{ xs: "column", sm: "row" }}
                        justifyContent="space-between"
                        spacing={1}
                        sx={{ mb: 2 }}
                      >
                        <Typography variant="subtitle2">
                          Student {index + 1}
                        </Typography>
                        <Button
                          type="button"
                          size="medium"
                          color="error"
                          variant="outlined"
                          onClick={() => removeStudent(student.id)}
                          disabled={meta.characterGroup.students.length === 1}
                        >
                          Remove
                        </Button>
                      </Stack>
                      <Box
                        sx={{
                          display: "grid",
                          gridTemplateColumns: {
                            xs: "1fr",
                            md: "repeat(2, minmax(0, 1fr))",
                          },
                          gap: 2,
                        }}
                      >
                        <TextField
                          label="Student Name"
                          value={student.name}
                          onChange={(event) =>
                            updateStudent(
                              student.id,
                              "name",
                              event.target.value,
                            )
                          }
                          fullWidth
                        />
                        <TextField
                          select
                          label="Student Image"
                          value={student.image}
                          onChange={(event) =>
                            updateStudent(
                              student.id,
                              "image",
                              event.target.value,
                            )
                          }
                          fullWidth
                        >
                          <MenuItem value="">None</MenuItem>
                          {renderBenchmarkImageOptions()}
                        </TextField>
                      </Box>
                    </Paper>
                  ))}
                </>
              )}
            </Stack>

            <Stack
              direction={{ xs: "column", sm: "row" }}
              alignItems={{ xs: "flex-start", sm: "center" }}
              justifyContent="space-between"
              spacing={1}
              sx={{ mt: 3, mb: 2 }}
            >
              <Typography variant="h6">
                Image Library ({benchmarkImageAssets.length})
              </Typography>
              <TextField
                value={imageLibrarySearch}
                onChange={(event) => setImageLibrarySearch(event.target.value)}
                placeholder="Search by filename, key, or URL"
                size="small"
                sx={{ minWidth: { sm: 300 } }}
              />
            </Stack>

            {(() => {
              const filteredImages = benchmarkImageAssets.filter(
                (asset, index) =>
                  matchesSearch(imageLibrarySearch, [
                    index + 1,
                    asset.fileName,
                    asset.key,
                    asset.publicUrl,
                  ]),
              );

              if (filteredImages.length === 0) {
                return (
                  <Typography variant="body2" color="text.secondary">
                    No images match the current search.
                  </Typography>
                );
              }

              return (
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: {
                      xs: "repeat(2, minmax(0, 1fr))",
                      sm: "repeat(4, minmax(0, 1fr))",
                      md: "repeat(10, minmax(0, 1fr))",
                    },
                    gap: 1.5,
                  }}
                >
                  {filteredImages.map((asset, index) => (
                    <Paper
                      key={asset.key}
                      variant="outlined"
                      sx={{ p: 1.25, minHeight: 110 }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          height: 64,
                        }}
                      >
                        <Box
                          component="img"
                          src={asset.publicUrl}
                          alt={benchmarkAssetLabel(asset)}
                          sx={{
                            display: "block",
                            maxWidth: "100%",
                            maxHeight: 56,
                            width: "auto",
                            borderRadius: 1,
                            objectFit: "contain",
                          }}
                        />
                      </Box>
                      <Typography
                        variant="caption"
                        sx={{
                          display: "block",
                          mt: 0.5,
                          textAlign: "center",
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                      >
                        {benchmarkAssetLabel(asset) || `Image ${index + 1}`}
                      </Typography>
                    </Paper>
                  ))}
                </Box>
              );
            })()}

            <Stack
              direction={{ xs: "column", sm: "row" }}
              alignItems={{ xs: "flex-start", sm: "center" }}
              justifyContent="space-between"
              spacing={1}
              sx={{ mt: 3, mb: 2 }}
            >
              <Typography variant="h6">
                Audio Library ({benchmarkAudioAssets.length})
              </Typography>
              <TextField
                value={audioLibrarySearch}
                onChange={(event) => setAudioLibrarySearch(event.target.value)}
                placeholder="Search by filename, key, or URL"
                size="small"
                sx={{ minWidth: { sm: 280 } }}
              />
            </Stack>

            {(() => {
              const filteredAudio = benchmarkAudioAssets.filter(
                (asset, index) =>
                  matchesSearch(audioLibrarySearch, [
                    index + 1,
                    asset.fileName,
                    asset.key,
                    asset.publicUrl,
                  ]),
              );

              if (filteredAudio.length === 0) {
                return (
                  <Typography variant="body2" color="text.secondary">
                    No audio items match the current search.
                  </Typography>
                );
              }

              return (
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: {
                      xs: "repeat(2, minmax(0, 1fr))",
                      md: "repeat(3, minmax(0, 1fr))",
                    },
                    gap: 1.5,
                  }}
                >
                  {filteredAudio.map((asset, index) => (
                    <Paper key={asset.key} variant="outlined" sx={{ p: 1.5 }}>
                      <Stack spacing={1}>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <AudiotrackIcon color="action" fontSize="small" />
                          <Typography
                            variant="body2"
                            sx={{
                              minWidth: 0,
                              whiteSpace: "nowrap",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                            }}
                          >
                            {benchmarkAssetLabel(asset) || `Audio ${index + 1}`}
                          </Typography>
                        </Stack>
                        <Box
                          component="audio"
                          controls
                          src={asset.publicUrl}
                          sx={{ width: "100%" }}
                        />
                      </Stack>
                    </Paper>
                  ))}
                </Box>
              );
            })()}
          </Paper>
        ) : null}

        {parts.map((part, partIndex) => {
          const tabId = `part-${partIndex}`;
          if (activeTab !== tabId) {
            return null;
          }

          const filteredYesNoQuestions = part.yesNoBank.filter(
            (question, questionIndex) =>
              matchesSearch(yesNoSearchByPart[partIndex] ?? "", [
                questionIndex + 1,
                question.id,
                question.prompt,
                question.optionA,
                question.optionB,
                question.image,
                question.audio,
              ]),
          );

          const filteredStudentSelectQuestions = part.studentSelectBank.filter(
            (question, questionIndex) =>
              matchesSearch(studentSelectSearchByPart[partIndex] ?? "", [
                questionIndex + 1,
                question.id,
                question.prompt,
                question.promptAudio,
                question.image,
                question.correctOption,
                ...question.options.flatMap((option) => [
                  option.id,
                  option.studentId,
                  option.audio,
                  meta.characterGroup.students.find(
                    (student) => student.id === option.studentId,
                  )?.name,
                ]),
              ]),
          );

          return (
            <Paper
              key={`part-section-${partIndex}`}
              variant="outlined"
              sx={{ p: 2.5, "& .MuiTextField-root": { minWidth: 0 } }}
            >
              <Stack spacing={3}>
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: {
                      xs: "1fr",
                      md: "repeat(3, minmax(0, 1fr))",
                      lg: "1.2fr 1fr 1fr",
                    },
                    gap: 2,
                  }}
                >
                  <TextField
                    label="Part Title"
                    value={part.title}
                    onChange={(event) =>
                      updatePartTitle(partIndex, event.target.value)
                    }
                    fullWidth
                  />
                  <TextField
                    select
                    label="Part Prompt Image"
                    value={part.promptImage}
                    onChange={(event) =>
                      updatePart(partIndex, (currentPart) => ({
                        ...currentPart,
                        promptImage: event.target.value,
                      }))
                    }
                    fullWidth
                  >
                    <MenuItem value="">None</MenuItem>
                    {renderBenchmarkImageOptions()}
                  </TextField>
                  <TextField
                    select
                    label="Part Prompt Audio"
                    value={part.promptAudio}
                    onChange={(event) =>
                      updatePart(partIndex, (currentPart) => ({
                        ...currentPart,
                        promptAudio: event.target.value,
                      }))
                    }
                    fullWidth
                  >
                    <MenuItem value="">None</MenuItem>
                    {renderBenchmarkAudioOptions()}
                  </TextField>
                </Box>

                <Stack spacing={2}>
                  <Stack
                    direction={{ xs: "column", sm: "row" }}
                    alignItems={{ xs: "flex-start", sm: "center" }}
                    justifyContent="space-between"
                    spacing={1}
                  >
                    <Typography variant="h6">Yes/No Question Bank</Typography>
                    <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
                      <TextField
                        value={yesNoSearchByPart[partIndex] ?? ""}
                        onChange={(event) =>
                          updateYesNoSearch(partIndex, event.target.value)
                        }
                        placeholder="Search prompt, answers, media, or ID"
                        size="small"
                        sx={{ minWidth: { sm: 280 } }}
                      />
                      <Button
                        type="button"
                        size="medium"
                        variant="outlined"
                        onClick={() =>
                          setAllYesNoCollapsedForPart(partIndex, true)
                        }
                      >
                        Collapse All
                      </Button>
                      <Button
                        type="button"
                        size="medium"
                        variant="outlined"
                        onClick={() =>
                          setAllYesNoCollapsedForPart(partIndex, false)
                        }
                      >
                        Expand All
                      </Button>
                      <Button
                        type="button"
                        size="medium"
                        variant="outlined"
                        onClick={() => addYesNoQuestion(partIndex)}
                      >
                        Add Yes/No Question
                      </Button>
                    </Stack>
                  </Stack>

                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: {
                        xs: "repeat(2, minmax(0, 1fr))",
                        sm: "repeat(4, minmax(0, 1fr))",
                        md: "repeat(10, minmax(0, 1fr))",
                      },
                      gap: 1.5,
                    }}
                  >
                    {filteredYesNoQuestions.map((question, questionIndex) => {
                      const isCollapsed = isYesNoCollapsed(
                        partIndex,
                        question.id,
                      );

                      if (isCollapsed) {
                        return (
                          <Paper
                            key={question.id}
                            variant="outlined"
                            sx={{
                              p: 1,
                              minHeight: 40,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              cursor: "pointer",
                            }}
                            onClick={() =>
                              toggleYesNoCollapsed(partIndex, question.id)
                            }
                          >
                            <Typography
                              variant="subtitle2"
                              sx={{
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                                maxWidth: "100%",
                              }}
                            >
                              {question.prompt ||
                                question.id ||
                                `Q${questionIndex + 1}`}
                            </Typography>
                          </Paper>
                        );
                      }

                      return (
                        <Paper
                          key={question.id}
                          variant="outlined"
                          sx={{ p: 1.75, gridColumn: "1 / -1" }}
                        >
                          <Stack
                            direction={{ xs: "column", sm: "row" }}
                            justifyContent="space-between"
                            spacing={1}
                            sx={{ mb: 2 }}
                          >
                            <Typography variant="subtitle1">
                              Yes/No Question {questionIndex + 1}
                            </Typography>
                            <Stack direction="row" spacing={1}>
                              <Button
                                type="button"
                                size="medium"
                                variant="outlined"
                                onClick={() =>
                                  toggleYesNoCollapsed(partIndex, question.id)
                                }
                              >
                                Collapse
                              </Button>
                              <Button
                                type="button"
                                size="medium"
                                color="error"
                                variant="outlined"
                                onClick={() =>
                                  removeYesNoQuestion(partIndex, question.id)
                                }
                                disabled={part.yesNoBank.length === 1}
                              >
                                Remove
                              </Button>
                            </Stack>
                          </Stack>

                          <Box
                            sx={{
                              display: "grid",
                              gridTemplateColumns: {
                                xs: "1fr",
                                md: "repeat(3, minmax(0, 1fr))",
                              },
                              gap: 2,
                            }}
                          >
                            <TextField
                              select
                              label="Image"
                              value={question.image}
                              onChange={(event) =>
                                updateYesNoQuestion(
                                  partIndex,
                                  question.id,
                                  "image",
                                  event.target.value,
                                )
                              }
                              fullWidth
                            >
                              <MenuItem value="">None</MenuItem>
                              {renderBenchmarkImageOptions()}
                            </TextField>

                            <TextField
                              select
                              label="Audio"
                              value={question.audio}
                              onChange={(event) =>
                                updateYesNoQuestion(
                                  partIndex,
                                  question.id,
                                  "audio",
                                  event.target.value,
                                )
                              }
                              fullWidth
                            >
                              <MenuItem value="">None</MenuItem>
                              {renderBenchmarkAudioOptions()}
                            </TextField>

                            <TextField
                              label="Question Prompt"
                              value={question.prompt}
                              onChange={(event) =>
                                updateYesNoQuestion(
                                  partIndex,
                                  question.id,
                                  "prompt",
                                  event.target.value,
                                )
                              }
                              rows={1}
                              fullWidth
                              sx={{ gridColumn: { xs: "auto", md: "1 / -1" } }}
                            />

                            <TextField
                              label="Option A Label"
                              value={question.optionA}
                              onChange={(event) =>
                                updateYesNoQuestion(
                                  partIndex,
                                  question.id,
                                  "optionA",
                                  event.target.value,
                                )
                              }
                              fullWidth
                            />

                            <TextField
                              label="Option B Label"
                              value={question.optionB}
                              onChange={(event) =>
                                updateYesNoQuestion(
                                  partIndex,
                                  question.id,
                                  "optionB",
                                  event.target.value,
                                )
                              }
                              fullWidth
                            />

                            <TextField
                              select
                              label="Correct Answer"
                              value={question.correctOption}
                              onChange={(event) =>
                                updateYesNoQuestion(
                                  partIndex,
                                  question.id,
                                  "correctOption",
                                  event.target.value,
                                )
                              }
                              fullWidth
                            >
                              <MenuItem value="A">A</MenuItem>
                              <MenuItem value="B">B</MenuItem>
                            </TextField>
                          </Box>
                          <AssessmentQuestionPreview
                            isVisible={isYesNoPreviewVisible(
                              partIndex,
                              question.id,
                            )}
                            onToggle={() =>
                              toggleYesNoPreview(partIndex, question.id)
                            }
                          >
                            <ListeningYesNoPreview
                              assessment={buildYesNoPreviewAssessment(
                                meta,
                                imageLibrary,
                                audioLibrary,
                                partIndex,
                                question,
                                questionIndex,
                              )}
                              isLoading={false}
                              isPreview
                              onStepReadyChange={() => {}}
                              onSaveAnswer={() => {}}
                            />
                          </AssessmentQuestionPreview>
                        </Paper>
                      );
                    })}
                    {filteredYesNoQuestions.length === 0 ? (
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ gridColumn: "1 / -1" }}
                      >
                        No yes/no questions match the current search.
                      </Typography>
                    ) : null}
                  </Box>
                </Stack>

                <Stack spacing={2}>
                  <Stack
                    direction={{ xs: "column", sm: "row" }}
                    alignItems={{ xs: "flex-start", sm: "center" }}
                    justifyContent="space-between"
                    spacing={1}
                  >
                    <Typography variant="h6">
                      Student Select Question Bank
                    </Typography>
                    <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
                      <TextField
                        value={studentSelectSearchByPart[partIndex] ?? ""}
                        onChange={(event) =>
                          updateStudentSelectSearch(
                            partIndex,
                            event.target.value,
                          )
                        }
                        placeholder="Search prompt, student, audio, or ID"
                        size="small"
                        sx={{ minWidth: { sm: 320 } }}
                      />
                      <Button
                        type="button"
                        size="medium"
                        variant="outlined"
                        onClick={() =>
                          setAllStudentSelectCollapsedForPart(partIndex, true)
                        }
                      >
                        Collapse All
                      </Button>
                      <Button
                        type="button"
                        size="medium"
                        variant="outlined"
                        onClick={() =>
                          setAllStudentSelectCollapsedForPart(partIndex, false)
                        }
                      >
                        Expand All
                      </Button>
                      <Button
                        type="button"
                        size="medium"
                        variant="outlined"
                        onClick={() => addStudentSelectQuestion(partIndex)}
                      >
                        Add Student Select Question
                      </Button>
                    </Stack>
                  </Stack>

                  {filteredStudentSelectQuestions.map(
                    (question, questionIndex) => (
                      <Paper key={question.id} variant="outlined" sx={{ p: 2 }}>
                        <Stack
                          direction={{ xs: "column", sm: "row" }}
                          justifyContent="space-between"
                          spacing={1}
                          sx={{ mb: 2 }}
                        >
                          <Typography variant="subtitle1">
                            Student Select Question {questionIndex + 1}
                          </Typography>
                          <Stack direction="row" spacing={1}>
                            <Button
                              type="button"
                              size="medium"
                              variant="outlined"
                              onClick={() =>
                                toggleStudentSelectCollapsed(
                                  partIndex,
                                  question.id,
                                )
                              }
                            >
                              {isStudentSelectCollapsed(partIndex, question.id)
                                ? "Expand"
                                : "Collapse"}
                            </Button>
                            <Button
                              type="button"
                              size="medium"
                              color="error"
                              variant="outlined"
                              onClick={() =>
                                removeStudentSelectQuestion(
                                  partIndex,
                                  question.id,
                                )
                              }
                              disabled={part.studentSelectBank.length === 1}
                            >
                              Remove
                            </Button>
                          </Stack>
                        </Stack>

                        {isStudentSelectCollapsed(partIndex, question.id) ? (
                          <Typography variant="body2" color="text.secondary">
                            Prompt: {question.prompt || "-"} | Correct Option:{" "}
                            {meta.characterGroup.students.find(
                              (student) =>
                                student.id === question.correctOption,
                            )?.name || "-"}{" "}
                            | Options: {question.options.length}
                          </Typography>
                        ) : (
                          <>
                            <Box
                              sx={{
                                display: "grid",
                                gridTemplateColumns: {
                                  xs: "1fr",
                                  md: "repeat(2, minmax(0, 1fr))",
                                },
                                gap: 2,
                                mb: 2,
                              }}
                            >
                              <TextField
                                select
                                label="Image"
                                value={question.image}
                                onChange={(event) =>
                                  updateStudentSelectQuestion(
                                    partIndex,
                                    question.id,
                                    "image",
                                    event.target.value,
                                  )
                                }
                                fullWidth
                              >
                                <MenuItem value="">None</MenuItem>
                                {renderBenchmarkImageOptions()}
                              </TextField>

                              <TextField
                                select
                                label="Prompt Audio"
                                value={question.promptAudio}
                                onChange={(event) =>
                                  updateStudentSelectQuestion(
                                    partIndex,
                                    question.id,
                                    "promptAudio",
                                    event.target.value,
                                  )
                                }
                                fullWidth
                              >
                                <MenuItem value="">None</MenuItem>
                                {renderBenchmarkAudioOptions()}
                              </TextField>

                              <TextField
                                label="Question Prompt"
                                value={question.prompt}
                                onChange={(event) =>
                                  updateStudentSelectQuestion(
                                    partIndex,
                                    question.id,
                                    "prompt",
                                    event.target.value,
                                  )
                                }
                                multiline
                                rows={2}
                                fullWidth
                                sx={{
                                  gridColumn: { xs: "auto", lg: "1 / -1" },
                                }}
                              />

                              <TextField
                                select
                                label="Correct Option"
                                value={question.correctOption}
                                onChange={(event) =>
                                  updateStudentSelectQuestion(
                                    partIndex,
                                    question.id,
                                    "correctOption",
                                    event.target.value,
                                  )
                                }
                                fullWidth
                              >
                                <MenuItem value="">None</MenuItem>
                                {question.options.map((option, optionIndex) => {
                                  const selectedStudent =
                                    meta.characterGroup.students.find(
                                      (student) =>
                                        student.id === option.studentId,
                                    );
                                  if (!selectedStudent) {
                                    return null;
                                  }
                                  return (
                                    <MenuItem
                                      key={option.id}
                                      value={selectedStudent.id}
                                    >
                                      {selectedStudent.name ||
                                        `Student ${optionIndex + 1}`}
                                    </MenuItem>
                                  );
                                })}
                              </TextField>
                            </Box>

                            <Stack
                              direction={{ xs: "column", sm: "row" }}
                              alignItems={{ xs: "flex-start", sm: "center" }}
                              justifyContent="space-between"
                              spacing={1}
                              sx={{ mb: 1 }}
                            >
                              <Typography variant="subtitle2">
                                Options
                              </Typography>
                              <Button
                                type="button"
                                size="medium"
                                variant="outlined"
                                onClick={() =>
                                  addStudentSelectOption(partIndex, question.id)
                                }
                              >
                                Add Option
                              </Button>
                            </Stack>

                            <Stack spacing={1.5}>
                              {question.options.map((option, optionIndex) => {
                                const selectedStudent =
                                  meta.characterGroup.students.find(
                                    (student) =>
                                      student.id === option.studentId,
                                  );
                                const selectedImage = selectedStudent
                                  ? imageLibrary.find(
                                      (media) =>
                                        media.id === selectedStudent.image,
                                    )
                                  : null;

                                return (
                                  <Paper
                                    key={option.id}
                                    variant="outlined"
                                    sx={{ p: 2 }}
                                  >
                                    <Stack
                                      direction={{ xs: "column", sm: "row" }}
                                      justifyContent="space-between"
                                      spacing={1}
                                      sx={{ mb: 2 }}
                                    >
                                      <Typography variant="body2">
                                        Option {optionIndex + 1}
                                      </Typography>
                                      <Button
                                        type="button"
                                        size="medium"
                                        color="error"
                                        variant="outlined"
                                        onClick={() =>
                                          removeStudentSelectOption(
                                            partIndex,
                                            question.id,
                                            option.id,
                                          )
                                        }
                                        disabled={question.options.length === 1}
                                      >
                                        Remove
                                      </Button>
                                    </Stack>

                                    <Box
                                      sx={{
                                        display: "grid",
                                        gridTemplateColumns: {
                                          xs: "1fr",
                                          md: "repeat(2, minmax(0, 1fr))",
                                        },
                                        gap: 2,
                                      }}
                                    >
                                      <TextField
                                        select
                                        label="Student"
                                        value={option.studentId}
                                        onChange={(event) =>
                                          updateStudentSelectOption(
                                            partIndex,
                                            question.id,
                                            option.id,
                                            "studentId",
                                            event.target.value,
                                          )
                                        }
                                        fullWidth
                                      >
                                        <MenuItem value="">None</MenuItem>
                                        {meta.characterGroup.students.map(
                                          (student, studentIndex) => (
                                            <MenuItem
                                              key={student.id}
                                              value={student.id}
                                            >
                                              {student.name ||
                                                `Student ${studentIndex + 1}`}
                                            </MenuItem>
                                          ),
                                        )}
                                      </TextField>

                                      <TextField
                                        select
                                        label="Option Audio"
                                        value={option.audio}
                                        onChange={(event) =>
                                          updateStudentSelectOption(
                                            partIndex,
                                            question.id,
                                            option.id,
                                            "audio",
                                            event.target.value,
                                          )
                                        }
                                        fullWidth
                                      >
                                        <MenuItem value="">None</MenuItem>
                                        {renderBenchmarkAudioOptions()}
                                      </TextField>

                                      <Box
                                        sx={{
                                          display: "flex",
                                          alignItems: "center",
                                        }}
                                      >
                                        {selectedImage?.imageUrl ? (
                                          <Box
                                            component="img"
                                            src={selectedImage.imageUrl}
                                            alt={
                                              selectedStudent?.name ||
                                              "Student preview"
                                            }
                                            sx={{
                                              display: "block",
                                              maxWidth: "100%",
                                              height: 48,
                                              width: "auto",
                                              border: "1px solid",
                                              borderColor: "divider",
                                              borderRadius: 1,
                                              objectFit: "contain",
                                              backgroundColor: "grey.50",
                                            }}
                                          />
                                        ) : (
                                          <Typography
                                            variant="caption"
                                            color="text.secondary"
                                          >
                                            Student image preview unavailable
                                          </Typography>
                                        )}
                                      </Box>
                                    </Box>
                                  </Paper>
                                );
                              })}
                            </Stack>
                            <AssessmentQuestionPreview
                              isVisible={isStudentSelectPreviewVisible(
                                partIndex,
                                question.id,
                              )}
                              onToggle={() =>
                                toggleStudentSelectPreview(
                                  partIndex,
                                  question.id,
                                )
                              }
                            >
                              <ListeningStudentSelectPreview
                                assessment={buildStudentSelectPreviewAssessment(
                                  meta,
                                  imageLibrary,
                                  audioLibrary,
                                  partIndex,
                                  question,
                                  questionIndex,
                                )}
                                isLoading={false}
                                isPreview
                                onStepReadyChange={() => {}}
                                onSaveAnswer={() => {}}
                              />
                            </AssessmentQuestionPreview>
                          </>
                        )}
                      </Paper>
                    ),
                  )}
                  {filteredStudentSelectQuestions.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">
                      No student select questions match the current search.
                    </Typography>
                  ) : null}
                </Stack>
              </Stack>
            </Paper>
          );
        })}
      </Stack>
      <Button
        type="button"
        size="large"
        variant="contained"
        onClick={() => saveTest()}
        disabled={isSavingTest}
        sx={{
          position: "fixed",
          right: { xs: 16, sm: 24 },
          bottom: { xs: 16, sm: 24 },
          zIndex: (theme) => theme.zIndex.tooltip,
          minWidth: 160,
          boxShadow: 6,
        }}
      >
        {isSavingTest ? "Saving..." : "Save Test"}
      </Button>
    </Container>
  );
}
